import React from "react";
import { Box, Divider, Theme, Typography, useMediaQuery } from "@mui/material";
import { useTranslation } from "react-i18next";
import truncate from "lodash/truncate";
import { Investment } from "@bn/api/apiv1";
import { getValueByInvestmentAndBookingType } from "@helpers";
import {
  CloseButton,
  GainLossPercents,
  LoadingPlaceHolder,
  Modal,
} from "@components";
import { GainLossTypography } from "@components/common/numbers";
import { SecurityImage } from "@components/SecurityImage";
import { useInvestmentLink } from "@hooks";
import { LinkWithRouting } from "@components/common/navigation";
import ExportIcon from "@icons/myicons/export.svg?react";
import { BookingsList } from "./BookingsList";

interface Props {
  investment: Investment | undefined;
  isOpen: boolean;
  onClose: () => void;
  image?: string | null;
  /** Set when opened from a single depot, so the investment page keeps it. */
  depotId?: number;
}

export const InvestmentDetailModal = ({
  investment,
  isOpen,
  onClose,
  image,
  depotId,
}: Props) => {
  const { t } = useTranslation();
  const isMobile = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("sm")
  );
  const getInvestmentUrl = useInvestmentLink(depotId);

  if (!investment)
    return (
      <Modal
        isOpen={isOpen}
        onClose={onClose}
        width={isMobile ? "auto" : 500}
        height="auto"
        maxHeight={isMobile ? "650px" : "100%"}
        position={isMobile ? "bottom" : "centered"}
        contentPadding={isMobile ? "16px 16px" : undefined}
        zIndex={10000}
        disablePortal={false}
      >
        <LoadingPlaceHolder />
      </Modal>
    );

  const quoteValue = getValueByInvestmentAndBookingType({
    value: investment.quote?.last ?? 0,
    investmentType: investment.type,
    currency: investment.currency,
    percentageRange: "0-100",
  });

  const instrumentUrl = getInvestmentUrl(investment);
  const modalPadding = isMobile ? 4 : 5;

  const header = (
    <>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        pb={4}
        pt={4}
        mt={-4}
        mx={-modalPadding}
        px={modalPadding}
        sx={{
          position: "sticky",
          top: (theme) => theme.spacing(-4),
          zIndex: 1,
          bgcolor: "background.paper",
        }}
      >
        <Box display="flex" alignItems="center" gap={4}>
          <SecurityImage
            src={image}
            width={32}
            height={32}
            alt={investment.standardisedName}
          />
          <Box>
            <Typography
              variant="h5"
              sx={{
                verticalAlign: "middle",
                display: "inline",
              }}
            >
              {truncate(investment.standardisedName, {
                length: 50,
                separator: " ",
              })}
            </Typography>
            {instrumentUrl && (
              <LinkWithRouting
                href={instrumentUrl}
                ml={2}
                sx={{
                  display: "inline-flex",
                  verticalAlign: "middle",
                  cursor: "pointer",
                  color: "text.primary",
                  "&:hover": { color: "primary.main" },
                }}
              >
                <ExportIcon width={24} height={24} />
              </LinkWithRouting>
            )}
          </Box>
        </Box>
        <CloseButton onClose={onClose} />
      </Box>
      <Box display="flex" mb={4}>
        <Box width="50%">
          <Typography variant="body4" color="textSecondary">
            {t("domains.investments.investmentTable.detailModal.currentQuote")}
          </Typography>
          <Typography variant="subtitle2">{quoteValue}</Typography>
        </Box>
        <Box width="50%">
          <Typography variant="body4" color="textSecondary">
            {t(
              "domains.investments.investmentTable.detailModal.performanceToday"
            )}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <GainLossTypography
              variant="subtitle2"
              value={investment.quote?.changeAbs ?? 0}
            >
              {investment.quote?.changeAbs != null
                ? getValueByInvestmentAndBookingType({
                    value: investment.quote.changeAbs,
                    investmentType: investment.type,
                    currency: investment.currency,
                    percentageRange: "0-100",
                  })
                : "–"}
            </GainLossTypography>
            <GainLossPercents value={investment.quote?.changePercentAbs ?? 0} />
          </Box>
        </Box>
      </Box>
    </>
  );

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={header}
      width={isMobile ? "auto" : 500}
      height="auto"
      maxHeight={isMobile ? "650px" : "100%"}
      position={isMobile ? "bottom" : "centered"}
      contentPadding={isMobile ? "16px 16px" : undefined}
      zIndex={10000}
      disablePortal={false}
    >
      <Box
        ml={-modalPadding}
        mr={-modalPadding}
        width={`calc(100% + ${modalPadding * 2} * 4px)`}
        display="flex"
        flexDirection="column"
        flex={1}
      >
        <Divider />
        <BookingsList investment={investment} image={image} />
      </Box>
    </Modal>
  );
};
